import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

/**
 * Saves a Blob/File and opens it.
 * On Web: Direct download.
 * On Mobile: Save to cache and open native share/view sheet.
 * 
 * @param {Blob} blob - The file content
 * @param {string} fileName - The suggested file name
 */
export const saveAndOpenFile = async (blob, fileName) => {
  const isMobile = Capacitor.isNativePlatform();
  // Sanitize filename for mobile filesystem
  const sanitizedFileName = fileName.replace(/[/\\?%*:|"<>]/g, '-');

  if (!isMobile) {
    // WEB DOWNLOAD LOGIC
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', sanitizedFileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 1000);
    return;
  }

  // MOBILE (CAPACITOR) LOGIC
  try {
    // 1. Convert Blob to Base64 string
    const base64Data = await blobToBase64(blob);

    // 2. Save the file to the Documents directory (more persistent than Cache)
    const savedFile = await Filesystem.writeFile({
      path: sanitizedFileName,
      data: base64Data,
      directory: Directory.Documents,
    });

    // 3. Use the Share API to open the file
    // Note: On Android, the share sheet allows "Save to device" or "Files by Google"
    await Share.share({
      title: sanitizedFileName,
      text: `Opening ${sanitizedFileName}`,
      url: savedFile.uri,
      dialogTitle: `Open ${sanitizedFileName}`,
    });
  } catch (error) {
    console.error('Error saving or sharing file:', error);
    throw error;
  }
};

/**
 * Helper to convert Blob to Base64
 */
const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      // FileReader result is "data:application/pdf;base64,..."
      // Capacitor Filesystem.writeFile expects only the base64 string
      const base64String = reader.result.split(',')[1];
      resolve(base64String);
    };
    reader.readAsDataURL(blob);
  });
};
